import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import TypographyField, {
  type TextStyle,
  type TypographyCatalog,
} from '../../components/typography/TypographyField';

/**
 * Typografie-Abschnitt des LP-Editors: die vier Element-Gruppen plus der Lead-Split-Schalter.
 *
 * Zwei Ebenen, sichtbar gemacht: der Scope-Default (zentral je Design-Variante) und der
 * Override dieser einen Seite. Gezeigt wird der Override; was der Default beisteuert, steht als
 * „(geerbt)" im Platzhalter. Ohne diese Unterscheidung wüsste niemand, ob ein Wert hier gilt,
 * weil er hier gesetzt wurde oder weil er zentral kommt — und „Zurücksetzen" wäre eine Wette.
 */

export interface LeadSplit {
  enabled?: boolean;
  color?: string;
}

/** Titelzeile unter der DOI-Headline. undefined = erbt, false = sichtbar, true = versteckt. */
export interface DoiTitle {
  hidden?: boolean;
}

export interface TypographyTokens {
  h1?: TextStyle;
  h2?: TextStyle;
  body?: TextStyle;
  cta?: TextStyle;
  leadSplit?: LeadSplit;
  doiTitle?: DoiTitle;
}

interface Props {
  /** Der Override DIESER Seite (nicht der zusammengeführte Wert). */
  value: TypographyTokens;
  onChange: (next: TypographyTokens) => void;
  catalog: TypographyCatalog;
  defaults: TypographyTokens;
  /** Nur für den Hinweistext: welche Variante liefert den Default. */
  scope: string;
}

const GROUPS: Array<{ key: 'h1' | 'h2' | 'body' | 'cta'; label: string; hint: string }> = [
  { key: 'h1', label: 'Headline (H1)', hint: 'Die große Überschrift im Kopfbereich.' },
  {
    key: 'h2',
    label: 'Subheadline & Zwischenüberschriften',
    hint: 'Unterzeile im Kopf plus die H2 in Karten, Bullet- und Verknappungsblöcken.',
  },
  {
    key: 'body',
    label: 'Fließtext & Bullets',
    hint: 'Der Textblock, die Referenten-Karte und die Häkchen-Liste.',
  },
  {
    key: 'cta',
    label: 'Button & Formular',
    hint: 'Anmelde-/Bestell-Button und die mitscrollende Leiste.',
  },
];

const HEX = /^#[0-9a-fA-F]{6}$/;

const hasAny = (s: TextStyle | undefined): boolean => Boolean(s && Object.keys(s).length);

const TypographyPanel: React.FC<Props> = ({ value, onChange, catalog, defaults, scope }) => {
  const ctaUnavailable = catalog.elements.includes('cta') && scope === 'doi';

  const setGroup = (key: 'h1' | 'h2' | 'body' | 'cta', next: TextStyle) => {
    const out: TypographyTokens = { ...value };
    if (Object.keys(next).length) out[key] = next;
    else delete out[key];
    onChange(out);
  };

  const setLead = (patch: LeadSplit) => {
    const merged: LeadSplit = { ...(value.leadSplit || {}), ...patch };
    const out: TypographyTokens = { ...value };
    // `enabled: false` ist eine Aussage, kein Leerwert — es schaltet den Split auf der
    // show-Variante AB und muss deshalb erhalten bleiben.
    if (merged.enabled === undefined && !merged.color) delete out.leadSplit;
    else out.leadSplit = merged;
    onChange(out);
  };

  // Dreiwertig: '' = erbt (Schluessel raus), 'an'/'aus' sind Aussagen und bleiben stehen.
  const setDoiTitel = (wahl: '' | 'an' | 'aus') => {
    const out: TypographyTokens = { ...value };
    if (wahl === '') delete out.doiTitle;
    else out.doiTitle = { hidden: wahl === 'aus' };
    onChange(out);
  };
  const doiTitelWahl: '' | 'an' | 'aus' =
    value.doiTitle?.hidden === undefined ? '' : value.doiTitle.hidden ? 'aus' : 'an';
  const doiTitelGeerbt = defaults.doiTitle?.hidden ? 'ausgeblendet' : 'sichtbar';

  const leadEnabled =
    value.leadSplit?.enabled ?? defaults.leadSplit?.enabled ?? scope === 'signup:show';
  const leadColor = value.leadSplit?.color ?? '';
  const leadColorMissing =
    leadEnabled && scope !== 'signup:show' && !leadColor && !defaults.leadSplit?.color;

  // Die beiden Schalter zaehlen mit: sonst meldet die Leiste „erbt komplett" und blendet den
  // Zuruecksetzen-Knopf aus, obwohl eine Abweichung gespeichert ist.
  const overriddenGroups =
    GROUPS.filter((g) => hasAny(value[g.key])).length +
    (value.leadSplit ? 1 : 0) +
    (value.doiTitle ? 1 : 0);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="subtitle2">Typografie</Typography>
        <Chip
          size="small"
          label={
            overriddenGroups
              ? `${overriddenGroups} Abweichung${overriddenGroups > 1 ? 'en' : ''}`
              : 'erbt komplett'
          }
          color={overriddenGroups ? 'warning' : 'default'}
        />
        {overriddenGroups > 0 && (
          <Button size="small" onClick={() => onChange({})}>
            Auf Varianten-Default zurücksetzen
          </Button>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Leer = der zentrale Wert der Variante <code>{scope}</code> gilt. Was hier steht, gilt nur
        für diese eine Seite.
      </Typography>

      {catalog.lockedReason && (
        <Alert severity="info" sx={{ mt: 0.5 }}>
          {catalog.lockedReason}
        </Alert>
      )}

      {GROUPS.map((g) => {
        const isCtaOnDoi = g.key === 'cta' && ctaUnavailable;
        return (
          <Accordion key={g.key} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">{g.label}</Typography>
              {hasAny(value[g.key]) && (
                <Chip size="small" label="abweichend" color="warning" sx={{ ml: 1 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>
              {isCtaOnDoi ? (
                <Alert severity="info">
                  Auf der Bestätigungsseite ist der einzige Button der Kalender-Button. Der bringt
                  sein eigenes Design mit und lässt sich von hier aus nicht umstellen.
                </Alert>
              ) : (
                <TypographyField
                  label={g.label}
                  hint={g.hint}
                  value={value[g.key] || {}}
                  onChange={(next) => setGroup(g.key, next)}
                  catalog={catalog}
                  inheritedFrom={defaults[g.key]}
                />
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {scope === 'doi' && (
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Webinar-Titel unter der Headline</Typography>
            {value.doiTitle && (
              <Chip size="small" label="abweichend" color="warning" sx={{ ml: 1 }} />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Typography variant="caption" color="text.secondary">
                Die Zeile unter der Headline zeigt den Webinar-Namen. Steht dort ein interner
                Arbeitstitel, kannst du sie hier ausblenden — oder besser im Webinar selbst einen
                Anzeigenamen setzen, der dann auch im Browser-Tab und im Kalendereintrag steht.
              </Typography>
              <TextField
                select
                size="small"
                label="Titelzeile"
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                value={doiTitelWahl}
                onChange={(e) => setDoiTitel(e.target.value as '' | 'an' | 'aus')}
                sx={{ width: 260 }}
              >
                <MenuItem value="">Erbt ({doiTitelGeerbt})</MenuItem>
                <MenuItem value="an">Anzeigen</MenuItem>
                <MenuItem value="aus">Ausblenden</MenuItem>
              </TextField>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">Erstes Wort abtrennen</Typography>
          {value.leadSplit && (
            <Chip size="small" label="abweichend" color="warning" sx={{ ml: 1 }} />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="caption" color="text.secondary">
              Setzt das erste Wort der Headline auf eine eigene Zeile und hebt den Rest farblich
              hervor. Bei der Variante „Show“ ist das seit jeher an.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={leadEnabled}
                  onChange={(e) => setLead({ enabled: e.target.checked })}
                />
              }
              label="Erstes Wort abtrennen, Rest hervorheben"
            />
            <Box display="flex" gap={1.5} alignItems="center">
              <TextField
                size="small"
                label="Farbe der Hervorhebung"
                value={leadColor}
                onChange={(e) => setLead({ color: e.target.value.trim() || undefined })}
                placeholder={defaults.leadSplit?.color || '#rrggbb'}
                InputLabelProps={{ shrink: true }}
                error={Boolean(leadColor) && !HEX.test(leadColor)}
                sx={{ width: 190 }}
                disabled={!leadEnabled}
              />
              <input
                type="color"
                aria-label="Farbe der Hervorhebung wählen"
                disabled={!leadEnabled}
                value={HEX.test(leadColor) ? leadColor : '#f3d27a'}
                onChange={(e) => setLead({ color: e.target.value })}
                style={{
                  width: 42,
                  height: 38,
                  padding: 0,
                  border: '1px solid #c4c4c4',
                  borderRadius: 4,
                  background: 'none',
                  cursor: leadEnabled ? 'pointer' : 'default',
                }}
              />
            </Box>
            {leadColorMissing && (
              <Alert severity="warning">
                Ohne Farbe bleibt die Hervorhebung in dieser Variante unsichtbar — nur „Show“ bringt
                dafür ein eigenes Gold mit.
              </Alert>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TypographyPanel;
